import * as React from 'react';

import { checkElementInViewport } from '../ElementInViewport/utils/checkElementInViewport';
import { getObserver } from '../ElementInViewport/utils';
import { trackMortgagePvzOffersButtonClick, trackMortgagePvzOffersButtonShow } from './tracking';

const PVZ_OFFERS_LINK_TEXT = 'Получить предложения банков';
const MAINPAGE_MORTGAGE_TAB_MARKER = 'mainpage_mortgage_tab';

function findPvzOffersLink(root: HTMLElement): HTMLAnchorElement | undefined {
  return [...root.querySelectorAll('a')].find(a => {
    const text = a.textContent?.trim();
    const href = a.getAttribute('href') ?? '';

    return text === PVZ_OFFERS_LINK_TEXT && href.includes(MAINPAGE_MORTGAGE_TAB_MARKER);
  });
}

export function useMortgagePvzOffersButtonAnalytics(containerRef: React.RefObject<HTMLElement | null>) {
  const hasSentShow = React.useRef(false);

  const fireShowOnce = React.useCallback(() => {
    if (hasSentShow.current) {
      return;
    }
    hasSentShow.current = true;
    trackMortgagePvzOffersButtonShow();
  }, []);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let detachClick: (() => void) | undefined;
    let detachViewport: (() => void) | undefined;
    let mutationObserver: MutationObserver | undefined;

    const attach = (link: HTMLAnchorElement) => {
      const onClickCapture = (event: MouseEvent) => {
        event.stopImmediatePropagation();
        trackMortgagePvzOffersButtonClick();
      };

      link.addEventListener('click', onClickCapture, { capture: true });

      if (checkElementInViewport(link, 0.5)) {
        fireShowOnce();
      }

      detachViewport = getObserver()({
        target: link,
        threshold: 0.5,
        onVisible: fireShowOnce,
      });

      detachClick = () => {
        link.removeEventListener('click', onClickCapture, { capture: true });
      };
    };

    const tryAttach = () => {
      const link = findPvzOffersLink(container);
      if (!link) {
        return false;
      }
      attach(link);
      mutationObserver?.disconnect();

      return true;
    };

    if (tryAttach()) {
      return () => {
        detachClick?.();
        detachViewport?.();
      };
    }

    mutationObserver = new MutationObserver(() => {
      tryAttach();
    });
    mutationObserver.observe(container, { childList: true, subtree: true });

    return () => {
      mutationObserver?.disconnect();
      detachClick?.();
      detachViewport?.();
    };
  }, [containerRef, fireShowOnce]);
}
